import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import {
  SECTION3_CONTACT_CHANNELS,
  SECTION3_NOT_QUALIFIED_REASONS,
  SECTION3_QUALIFIED_REASONS,
  section3StatusLabel,
  section3StatusFromError,
  section3StatusTone,
} from './section3Status';

export const section3QueryKeys = {
  root: ['section3'] as const,
  health: ['section3', 'health'] as const,
  lead: (leadId: string | null) => ['section3', 'lead', leadId] as const,
  routing: (leadId: string | null) => ['section3', 'lead', leadId, 'routing'] as const,
  timeline: (leadId: string | null) => ['section3', 'lead', leadId, 'timeline'] as const,
} as const;

export { SECTION3_CONTACT_CHANNELS, SECTION3_QUALIFIED_REASONS, SECTION3_NOT_QUALIFIED_REASONS, section3StatusTone, section3StatusLabel };

export { section3StatusFromError };

function idempotencyKey() {
  return globalThis.crypto?.randomUUID?.() ?? `section3-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export interface Section3HealthResponse {
  ok: true;
  scope: 'all' | 'team' | 'self';
  routingEnabled: boolean;
  workersEnabled: boolean;
}

export interface Section3RoutingSummary {
  ok: true;
  routing: null | {
    id: string;
    outcome: string;
    tier: string | null;
    assignedUserId: string | null;
    failureCode: string;
    committedAt: string;
  };
  sla: null | {
    assignedAt: string;
    dueAt: string;
    slaMinutes: number;
    firstContactAt: string | null;
    status: 'pending' | 'met' | 'breached';
  };
  nurture: null | {
    id: string;
    status: string;
    sequenceId: string;
  };
}

export interface Section3TimelineEvent {
  id: string;
  eventType: 'contact' | 'assignment' | 'qualification' | 'sla_breached' | string;
  channel: string | null;
  humanOrigin: boolean;
  occurredAt: string;
  sourceType: string;
  sourceRecordId: string;
  data: { outcome?: string; reasonCode?: string };
}

export interface Section3TimelineResponse {
  ok: true;
  events: Section3TimelineEvent[];
  nextCursor: string | null;
}

export interface Section3ContactEventResponse {
  ok: true;
  event: {
    id: string;
    eventType: 'contact';
    channel: 'call' | 'whatsapp' | 'email';
    occurredAt: string;
    humanOrigin: boolean;
    sourceType: string;
    sourceRecordId: string;
  };
  sla: null | {
    firstContactAt: string | null;
    dueAt: string;
    status: 'pending' | 'met' | 'breached';
  };
}

export interface Section3QualificationResponse {
  ok: boolean;
  qualification: null | {
    id: string;
    outcome: 'qualified' | 'not-qualified';
    reasonCode: string;
    decidedAt: string;
    meetingId: string | null;
    nurtureEnrollmentId: string | null;
    handoffStatus: string;
  };
  failure?: { status: 'pending-manual'; reasonCode: string };
}

export function useSection3Health() {
  return useQuery({
    queryKey: section3QueryKeys.health,
    queryFn: async () => {
      const { data } = await api.get<Section3HealthResponse>('/api/v2/section3/health');
      return data;
    },
    staleTime: 60_000,
  });
}

export function useLeadRoutingSummary(leadId: string | null) {
  return useQuery({
    queryKey: section3QueryKeys.routing(leadId),
    queryFn: async () => {
      const { data } = await api.get<Section3RoutingSummary>(`/api/v2/leads/${leadId}/routing`);
      return data;
    },
    enabled: !!leadId,
    placeholderData: keepPreviousData,
  });
}

export function useLeadTimeline(leadId: string | null, cursor?: string | null) {
  return useQuery({
    queryKey: ['section3', 'lead', leadId, 'timeline', cursor ?? null],
    queryFn: async () => {
      const suffix = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
      const { data } = await api.get<Section3TimelineResponse>(`/api/v2/leads/${leadId}/timeline${suffix}`);
      return data;
    },
    enabled: !!leadId,
    placeholderData: keepPreviousData,
  });
}

export function useSection3LeadMutations(leadId: string | null) {
  const queryClient = useQueryClient();
  const invalidateLead = () => {
    queryClient.invalidateQueries({ queryKey: section3QueryKeys.lead(leadId) });
    queryClient.invalidateQueries({ queryKey: section3QueryKeys.routing(leadId) });
    queryClient.invalidateQueries({ queryKey: section3QueryKeys.timeline(leadId) });
  };

  const recordContactEvent = useMutation({
    mutationFn: async (channel: 'call' | 'whatsapp' | 'email') => {
      if (!leadId) throw new Error('Lead is missing.');
      const { data } = await api.post<Section3ContactEventResponse>(
        `/api/v2/leads/${leadId}/contact-events`,
        {
          channel,
          occurredAt: new Date().toISOString(),
          humanOrigin: true,
          sourceType: 'LeadDrawer',
          sourceRecordId: `${leadId}:${channel}`,
        },
        { headers: { 'Idempotency-Key': idempotencyKey() } },
      );
      return data;
    },
    onSuccess: invalidateLead,
  });

  const qualifyLead = useMutation({
    mutationFn: async (payload: {
      outcome: 'qualified' | 'not-qualified';
      reasonCode: string;
      meeting?: { title: string; scheduledAt?: string; durationMinutes?: number; platform?: string } | null;
    }) => {
      if (!leadId) throw new Error('Lead is missing.');
      const { data } = await api.post<Section3QualificationResponse>(
        `/api/v2/leads/${leadId}/qualification`,
        payload,
        { headers: { 'Idempotency-Key': idempotencyKey() } },
      );
      return data;
    },
    onSuccess: invalidateLead,
  });

  const routeLead = useMutation({
    mutationFn: async () => {
      if (!leadId) throw new Error('Lead is missing.');
      const { data } = await api.post<Section3RoutingSummary>(
        `/api/v2/leads/${leadId}/routing`,
        {},
        { headers: { 'Idempotency-Key': idempotencyKey() } },
      );
      return data;
    },
    onSuccess: invalidateLead,
  });

  const retryRouting = useMutation({
    mutationFn: async () => {
      if (!leadId) throw new Error('Lead is missing.');
      const { data } = await api.post<Section3RoutingSummary>(
        `/api/v2/leads/${leadId}/routing/retry`,
        {},
        { headers: { 'Idempotency-Key': idempotencyKey() } },
      );
      return data;
    },
    onSuccess: invalidateLead,
  });

  return { recordContactEvent, qualifyLead, routeLead, retryRouting };
}

export function section3ErrorMessage(error: unknown): string {
  return getApiErrorMessage(error, 'Section 3 action failed.');
}


// ── Section 5 additional screens (24.3 Quotation, 24.4 Deals outcomes, 24.5 Analytics) ──
// These call the Section 3 feature endpoints. Resource reads are negative by
// default: missing and out-of-scope records both surface as 404, which the UI
// maps to an honest unavailable notice without fabricating data.

export interface Section3AnalyticsSummary {
  ok: true;
  scope: 'all' | 'team' | 'self';
  canSelectScope: boolean;
  lossCountByReason: { reasonCode: string; count: number }[];
  lostValueByCurrency: { currency: string; amountMinor: number }[];
  wonLostRatioText: string;
  winPercentage: number | null;
  structuredDataCoverage: { total: number; structured: number } | null;
  bdeFirstResponseKpi: { medianMinutes: number | null } | null;
  conversionKpi: { rate: number | null } | null;
  openPipelineByCurrency: { currency: string; amountMinor: number }[];
  overdueFollowUpCount: number;
}

export function useSection3AnalyticsSummary(enabled = true) {
  return useQuery({
    queryKey: ['section3', 'analytics', 'summary'],
    queryFn: async () => {
      const { data } = await api.get<Section3AnalyticsSummary>('/api/v2/section3/analytics/summary');
      return data;
    },
    enabled,
    retry: false,
  });
}

export interface QuotationRevision {
  id: string;
  revisionNumber: number;
  currency: string;
  totalMinor: number;
  approvalState: 'none' | 'required' | 'approved' | 'rejected';
  createdAt: string;
  lines: { serviceLine: string; quantity: number; unitPriceMinor: number; discountMinor: number }[];
}

export interface QuotationWorkspaceResponse {
  ok: true;
  salesDocId: string;
  currency: string;
  revisions: QuotationRevision[];
  approvalRequired: boolean;
  canApprove: boolean;
  deliveryEvidence: { sentAt: string | null; evidenceRef: string | null } | null;
}

export function useQuotationWorkspace(salesDocId: string | null) {
  return useQuery({
    queryKey: ['section3', 'quotation', salesDocId],
    queryFn: async () => {
      const { data } = await api.get<QuotationWorkspaceResponse>(`/api/v2/salesdocs/${salesDocId}/quotation`);
      return data;
    },
    enabled: !!salesDocId,
    retry: false,
  });
}

export interface DealOutcomeResponse {
  ok: true;
  dealId: string;
  stage: 'prospecting' | 'qualification' | 'proposal' | 'negotiation' | 'closed_won' | 'closed_lost';
  acceptedQuotationId: string | null;
  approvalGateMet: boolean;
  deliveryGateMet: boolean;
  sharedClientStatus: string | null;
  accountsHandoffStatus: string | null;
  portalHandoffStatus: string | null;
  outboxStatus: string | null;
  pendingManual: boolean;
  lostReason: string | null;
}

export function useDealOutcome(dealId: string | null) {
  return useQuery({
    queryKey: ['section3', 'deal-outcome', dealId],
    queryFn: async () => {
      const { data } = await api.get<DealOutcomeResponse>(`/api/v2/deals/${dealId}/outcome`);
      return data;
    },
    enabled: !!dealId,
    retry: false,
  });
}
